import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Cpu, User, Phone, Mail, MapPin, Lock, ArrowLeft, Gift, CheckCircle, MailCheck } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ClientRegisterPage = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    nom: '',
    telephone: '',
    email: '',
    adresse: '',
    password: '',
    confirmPassword: '',
    referral_code: searchParams.get('ref') || '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [referralValid, setReferralValid] = useState(null);
  const [referralNom, setReferralNom] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Validate referral code on load if present
  useEffect(() => {
    const code = searchParams.get('ref');
    if (code) {
      axios.get(`${API}/api/loyalty/referral/validate/${code}`).then(res => {
        setReferralValid(true);
        setReferralNom(res.data.referrer_nom || '');
      }).catch(() => {
        setReferralValid(false);
      });
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.email || !formData.email.trim()) {
      setError(t('register.emailRequired'));
      return;
    }

    const password = formData.password;

    if (!password || password.length < 4) {
      setError(t('register.passwordMinLength'));
      return;
    }

    if (password !== formData.confirmPassword) {
      setError(t('register.passwordMismatch'));
      return;
    }

    setLoading(true);
    
    try {
      const res = await axios.post(`${API}/api/client/register`, {
        nom: formData.nom,
        telephone: formData.telephone,
        email: formData.email.trim().toLowerCase(),
        adresse: formData.adresse,
        password,
        type_client: 'Particulier',
        referral_code: formData.referral_code,
      });

      if (res.data.status === 'verification_required') {
        setRegistrationSuccess(true);
        setRegisteredEmail(res.data.email || formData.email);
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('register.registerError'));
    }
    
    setLoading(false);
  };

  // Success screen after registration
  if (registrationSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" data-testid="registration-success">
        <div className="grid-pattern"></div>
        <div className="glass p-8 w-full max-w-md fade-in text-center">
          <div className="w-20 h-20 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-4">
            <MailCheck className="w-10 h-10 text-cyan-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">{t('register.verifyEmailTitle')}</h2>
          <p className="text-gray-400 text-sm mb-2">
            {t('register.confirmationSentTo')}
          </p>
          <p className="text-cyan-400 font-semibold mb-6" data-testid="registered-email">
            {registeredEmail}
          </p>
          <p className="text-gray-500 text-xs mb-6">
            {t('register.activateHint')}
          </p>
          <Link
            to="/login/client"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white btn-cyan"
            data-testid="go-to-login-btn"
          >
            {t('register.goToLogin')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="grid-pattern"></div>
      <div className="absolute top-4 right-4 z-30"><LanguageSelector /></div>
      
      <div className="glass p-8 w-full max-w-md fade-in">
        <Link to="/login" className="inline-flex items-center text-gray-400 hover:text-cyan-400 mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('register.login')}
        </Link>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 mb-4">
            <Cpu className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
            {t('register.clientTitle')}
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            {t('register.clientSubtitle')}
          </p>
        </div>

        {error && (
          <div className="alert-danger-custom mb-4 text-center" data-testid="register-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
          <div>
            <label className="block text-sm text-gray-300 mb-2">{t('register.fullName')} *</label>
            <div className="relative">
              <input
                type="text"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                className="input-dark w-full pl-10 pr-4 py-3"
                placeholder={t('register.namePlaceholder')}
                required
                data-testid="register-nom"
              />
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-2">{t('register.phone')} *</label>
            <div className="relative">
              <input
                type="tel"
                name="telephone"
                value={formData.telephone}
                onChange={handleChange}
                className="input-dark w-full pl-10 pr-4 py-3"
                placeholder="0694 12 34 56"
                required
                data-testid="register-telephone"
              />
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-2">{t('register.email')} *</label>
            <div className="relative">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="input-dark w-full pl-10 pr-4 py-3"
                placeholder={t('register.emailPlaceholder')}
                required
                data-testid="register-email"
              />
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            </div>
            <p className="text-xs text-gray-500 mt-1">{t('register.emailHint')}</p>
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-2">{t('register.address')}</label>
            <div className="relative">
              <input
                type="text"
                name="adresse"
                value={formData.adresse}
                onChange={handleChange}
                className="input-dark w-full pl-10 pr-4 py-3"
                placeholder={t('register.addressPlaceholder')}
                data-testid="register-adresse"
              />
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-2">{t('register.password')} *</label>
            <div className="relative">
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="input-dark w-full pl-10 pr-4 py-3"
                placeholder="********"
                required
                data-testid="register-password"
              />
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-2">{t('register.confirmPassword')} *</label>
            <div className="relative">
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="input-dark w-full pl-10 pr-4 py-3"
                placeholder="********"
                required
                data-testid="register-confirm-password"
              />
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            </div>
          </div>

          {/* Referral Code */}
          <div>
            <label className="block text-sm text-gray-300 mb-2">
              <Gift className="w-3.5 h-3.5 inline mr-1" />{t('register.referralCode')}
            </label>
            <div className="relative">
              <input
                type="text"
                name="referral_code"
                value={formData.referral_code}
                onChange={(e) => {
                  const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
                  setFormData({ ...formData, referral_code: val });
                  setReferralValid(null);
                  if (val.length === 6) {
                    axios.get(`${API}/api/loyalty/referral/validate/${val}`).then(res => {
                      setReferralValid(true);
                      setReferralNom(res.data.referrer_nom || '');
                    }).catch(() => setReferralValid(false));
                  }
                }}
                className="input-dark w-full pl-10 pr-4 py-3 font-mono tracking-widest uppercase"
                placeholder="ABC123"
                maxLength={6}
                data-testid="referral-code-input"
              />
              <Gift className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              {referralValid === true && (
                <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-400" />
              )}
            </div>
            {referralValid === true && referralNom && (
              <p className="text-xs text-green-400 mt-1">{t('register.referredBy', { nom: referralNom })}</p>
            )}
            {referralValid === false && formData.referral_code.length === 6 && (
              <p className="text-xs text-red-400 mt-1">{t('register.invalidReferral')}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 rounded-xl font-bold text-white btn-cyan mt-6 ${
              loading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            data-testid="register-submit-btn"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                {t('register.creating')}
              </span>
            ) : (
              t('register.createAccount')
            )}
          </button>
        </form>

        <p className="text-center mt-6 text-xs text-gray-600">
          {t('register.termsNotice')}
        </p>
      </div>
    </div>
  );
};

export default ClientRegisterPage;
